"use client";

import { buildInspectionPortalLicenseUrl } from "@/components/inspection/dispatch-page";
import { Button } from "@/components/ui/button";
import type { RegistrantLicenseListLicenseResponseSchema } from "@/hera-api/client/types.gen";
import { ensureInspectionLicenseAccess } from "@/lib/inspection-portal-api";
import { cn } from "@/lib/utils";
import { useAuth0 } from "@auth0/auth0-react";
import { useMutation } from "@tanstack/react-query";
import { ClipboardCheck } from "lucide-react";
import { toast } from "sonner";

/** True when the license list row has a pending inspection that can be performed. */
export function canPerformInspection(
  license: Pick<
    RegistrantLicenseListLicenseResponseSchema,
    "newest_inspection_status"
  >,
): boolean {
  return license.newest_inspection_status === "INSPECTION_PENDING";
}

type PerformInspectionButtonProps = {
  licenseId: string;
  className?: string;
  /** Render as a compact square icon button with the label in a tooltip. */
  iconOnly?: boolean;
};

/**
 * Opens the field inspection perform workflow: ensure-access then hard-navigate
 * to `/inspection/{licenseId}/perform` (same path as the Inspections tab).
 */
export function PerformInspectionButton({
  licenseId,
  className,
  iconOnly = false,
}: PerformInspectionButtonProps) {
  const { getAccessTokenSilently } = useAuth0();

  const performMutation = useMutation({
    mutationFn: async () => {
      await ensureInspectionLicenseAccess(() => getAccessTokenSilently(), {
        license_ids: [licenseId],
      });
      return licenseId;
    },
    onSuccess: (id) => {
      window.location.assign(buildInspectionPortalLicenseUrl(id));
    },
    onError: (err) => {
      toast.error("Unable to open inspection", {
        description: err instanceof Error ? err.message : "Unknown error",
      });
    },
  });

  if (iconOnly) {
    return (
      <Button
        type="button"
        variant="outline"
        size="icon"
        className={cn("size-6 p-0", className)}
        disabled={performMutation.isPending}
        onClick={() => performMutation.mutate()}
        title="Perform Inspection"
        aria-label="Perform Inspection"
      >
        <ClipboardCheck className="h-4 w-4" />
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      className={className}
      disabled={performMutation.isPending}
      onClick={() => performMutation.mutate()}
    >
      <ClipboardCheck className="h-4 w-4" />
      {performMutation.isPending ? "Opening…" : "Perform Inspection"}
    </Button>
  );
}
